import { normalizeTemplateDatastreamCollection } from '../../utils/datastreamValidation';
import { normalizeWebDashboardTabs } from '../../utils/webDashboardLayout';

export const getTabPopupPosition = (rect, { width = 340, estimatedHeight = 600 } = {}) => {
    if (!rect || typeof window === 'undefined') {
        return { top: 12, left: width / 2 + 12 };
    }

    const margin = 12;
    const preferredTop = rect.bottom + 8;
    const maxTop = Math.max(margin, window.innerHeight - margin - estimatedHeight);
    const minLeft = margin + width / 2;
    const maxLeft = Math.max(minLeft, window.innerWidth - margin - width / 2);
    const preferredLeft = rect.left + rect.width / 2;

    return {
        top: Math.max(margin, Math.min(preferredTop, maxTop)),
        left: Math.max(minLeft, Math.min(preferredLeft, maxLeft)),
    };
};

export const normalizeExampleNameValue = (value) => String(value ?? '').trim().toLowerCase();

export const sortTemplateExamples = (examples) =>
    [...(Array.isArray(examples) ? examples : [])].sort((left, right) =>
        String(left?.example_name || '').localeCompare(String(right?.example_name || ''), undefined, {
            sensitivity: 'base',
        })
    );

export const buildTemplateExampleSelectStyles = () => ({
    control: (provided, state) => ({
        ...provided,
        backgroundColor: 'var(--rs-control-bg)',
        borderColor: state.isFocused
            ? 'var(--rs-control-border-focus)'
            : 'var(--rs-control-border)',
        boxShadow: state.isFocused ? 'var(--rs-control-focus-shadow)' : 'none',
        '&:hover': {
            borderColor: 'var(--rs-control-border-hover)',
        },
        color: 'var(--rs-text)',
        fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
        minHeight: '44px',
        borderRadius: 8,
        cursor: 'pointer',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: 'var(--rs-text)',
        fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    }),
    input: (provided) => ({
        ...provided,
        color: 'var(--rs-text)',
        fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: 'var(--rs-placeholder)',
        fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: 'var(--rs-menu-bg)',
        border: '1px solid var(--rs-menu-border)',
        borderRadius: 8,
        overflow: 'hidden',
        zIndex: 9999,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? 'var(--rs-option-selected-bg)'
            : state.isFocused
            ? 'var(--rs-option-hover-bg)'
            : 'var(--rs-option-bg)',
        color: state.isSelected ? 'var(--rs-option-selected-text)' : 'var(--rs-text)',
        fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
        '&:active': {
            backgroundColor: 'var(--rs-option-active-bg)',
        },
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: 'var(--rs-text)',
        '&:hover': {
            color: 'var(--rs-control-border-focus)',
        },
    }),
    clearIndicator: (provided) => ({
        ...provided,
        color: 'var(--rs-text)',
        '&:hover': {
            color: 'var(--rs-control-border-focus)',
        },
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
});

export const templateDetailsPagingOptions = [10, 20, 50, 100];

export const templateDetailsTableColors = {
    headerBackground: 'var(--header-background)',
    headerColor: 'var(--header-color)',
    headerHover: 'var(--header-hover)',
    cellBackground: 'var(--cell-background)',
    cellColor: 'var(--cell-color)',
    cellHover: 'var(--cell-hover)',
    orderColor: 'var(--order-color)',
    orderSelectedColor: 'rgb(49, 199, 149)',
    checkboxSelection: 'var(--accent)',
    btnBackground: 'rgb(219, 245, 236)',
    btnColor: 'var(--accent)',
    modalBorder: 'var(--modal-border)',
    red: 'rgb(236, 69, 78)'
};

export const buildDisabledActionsColumn = (headerName) => ({
    field: 'actions',
    headerName,
    width: 100,
    cellClassName: 'actions-disabled',
    renderCell: () => null
});

export const buildTemplateFileActionMessages = (t) => ({
    deletedFiles: t.deletedFiles,
    imageCleanupWarning: t.imageCleanupWarning,
    invalidFilenameForDeletion: t.invalidFilenameForDeletion,
    fileDeletedSuccess: t.fileDeletedSuccess,
    invalidUploadFile: t.invalidUploadFile,
    fileUploadedSuccess: t.fileUploadedSuccess,
});

const parseTemplateArrayField = (value) =>
    (typeof value === 'string' ? JSON.parse(value) : (value || [])).filter(Boolean);

export const hydrateFetchedTemplateDetails = (templateData) => {
    const parsedDatastreams = normalizeTemplateDatastreamCollection(
        typeof templateData.datastreams === 'string'
            ? JSON.parse(templateData.datastreams)
            : (templateData.datastreams || [])
    );
    const parsedEvents = parseTemplateArrayField(templateData.events);
    const allWebdashboardsRaw = parseTemplateArrayField(templateData.webdashboard);
    const processedWebdashboards = normalizeWebDashboardTabs(allWebdashboardsRaw);
    const parsedMobdashboard = parseTemplateArrayField(templateData.mobdashboard);
    const evaluationData = templateData.templateEvaluationData || { evaluation_names: [], datastream_ranges: [] };
    const templateDevices = Array.isArray(templateData.templateDevices)
        ? templateData.templateDevices
        : (Array.isArray(templateData.devices) ? templateData.devices : []);

    const finalTemplateState = {
        ...templateData,
        datastreams: parsedDatastreams,
        events: parsedEvents,
        webdashboard: processedWebdashboards,
        mobdashboard: parsedMobdashboard,
        templateDevices,
        devices: templateDevices
    };

    return {
        evaluationData,
        hydrated: { ...finalTemplateState, templateEvaluationData: evaluationData },
    };
};

export const getOriginDevicePath = (origin, originDeviceId) =>
    origin === 'device' && originDeviceId ? `/devices/details/${originDeviceId}` : null;

export const normalizeIconPath = (value) => {
    if (!value || typeof value !== "string") return null;

    let v = value.trim().split("?")[0].replace(/\\/g, "/").replace(/^\/+/, "");

    try {
        if (v.startsWith("http://") || v.startsWith("https://")) {
            const u = new URL(v);
            v = u.pathname.replace(/^\/+/, "");
        }
    } catch {}

    if (v.startsWith("icons/")) return v;

    return `icons/${v}`;
};

export const extractDashboardFiles = (templateObj) => {
    const files = new Set();
    const webdashboard = templateObj?.webdashboard || [];

    for (const page of webdashboard) {
        const widgets = page?.widgets || [];
        for (const widget of widgets) {
            if (widget?.type === "background-image") {
                [
                    widget?.extra_props?.imageUrl,
                    widget?.extra_props?.lightImageUrl,
                    widget?.extra_props?.darkImageUrl,
                ].forEach((img) => {
                    const normalized = normalizeIconPath(img);
                    if (normalized) files.add(normalized);
                });
            }

            if (widget?.type === "image-button") {
                const images = widget?.extra_props?.images || [];
                for (const imgObj of images) {
                    const img = imgObj?.imageUrl;
                    const normalized = normalizeIconPath(img);
                    if (normalized) files.add(normalized);
                }
            }
        }
    }
    return files;
};

export const diffRemovedFiles = (beforeTemplate, afterTemplate) => {
    const before = extractDashboardFiles(beforeTemplate);
    const after = extractDashboardFiles(afterTemplate);

    const removed = [];
    for (const f of before) {
        if (!after.has(f)) removed.push(f);
    }
    return removed;
};

export const stripWidgetMinMaxUnit = (pages) => {
    const TYPES = new Set(['gauge', 'radial-gauge', 'number', 'slider', 'label']);
    return (pages || []).map((p) => {
        const widgets = (p?.widgets || []).map((w) => {
            if (!w || !TYPES.has(w.type) || !w.extra_props) return w;
            const ep = { ...(w.extra_props || {}) };
            delete ep.min;
            delete ep.max;
            delete ep.unit;
            return { ...w, extra_props: ep };
        });
        return { ...p, widgets };
    });
};

export const cloneTemplateDetailsForWidgetUpload = (details) => ({
    ...details,
    webdashboard: (details.webdashboard || []).map((page) => ({
        ...page,
        widgets: (page.widgets || []).map((widget) => ({
            ...widget,
            extra_props: widget?.extra_props ? { ...widget.extra_props } : widget?.extra_props,
        })),
    })),
});

export const buildDatastreamValidationOptions = (t, absLimit) => ({
    invalidCollection:
        (t && t.invalidDatastreamCollection) ||
        "Datastreams payload is invalid.",
    invalidDatastream:
        (t && t.invalidDatastreamRow) ||
        "Datastream at row {row} is invalid.",
    minMaxRequired:
        (t && t.minMaxUnitRequired) ||
        "Min and Max are required and must be valid numbers.",
    outOfBounds:
        (t && t.minMaxOutOfBounds) ||
        `Min and Max must be between -${absLimit.toLocaleString()} and ${absLimit.toLocaleString()}.`,
    minLessThanMax:
        (t && t.minLessThanMax) || "Min must be less than Max.",
    defaultInvalid:
        (t && t.defaultValueInvalid) || "Default value must be a valid number.",
    defaultOutOfRange:
        (t && t.defaultValueOutOfRange) || "Default value must be between Min and Max.",
    duplicateTitle:
        (t && t.datastreamTitleUnique) ||
        'Datastream title "{title}" is already used by datastream "{conflict}". Please choose another title for datastream "{target}".',
    datastreamPrefix:
        (t && t.datastreamLabel) || "Datastream",
});

export const buildDatastreamTypeChangeConfirmation = (t, datastreamTypeChanges) => {
    const firstChange = datastreamTypeChanges[0];
    const firstDatastreamName = firstChange.name || firstChange.pin || 'Datastream';
    const changeList = datastreamTypeChanges
        .map((change) => {
            const datastreamName = change.name || change.pin || 'Datastream';
            const pinLabel = change.pin ? ` (${change.pin})` : '';
            return `"${datastreamName}"${pinLabel}: ${change.fromType || 'empty'} -> ${change.toType || 'empty'}`;
        })
        .join('\n');
    const defaultTypeChangeMessage = datastreamTypeChanges.length === 1
        ? `Are you sure you want to change the datastream "${firstDatastreamName}" type?\n\nHistorical data for pin "${firstChange.pin || ''}" will be cleaned in the background after the template is saved. A safe backup is kept if cleanup fails, and you will receive a system notification with the result.`
        : `Are you sure you want to change these datastream types?\n\n{changes}\n\nHistorical data for the affected pins will be cleaned in the background after the template is saved. A safe backup is kept if cleanup fails, and you will receive a system notification with the result.`;

    return {
        type: 'confirm',
        variant: 'danger',
        icon: 'delete',
        title: t.datastreamTypeChangeConfirmTitle || 'Change datastream type?',
        message:
            (t.datastreamTypeChangeConfirmMessage ||
                defaultTypeChangeMessage)
                .replace('{changes}', changeList),
        confirmText: t.datastreamTypeChangeConfirmButton || 'Change type and start cleanup',
        cancelText: t.buttonCancel || 'Cancel',
        showCancel: true,
    };
};

const normalizeDatastreamIdKey = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === '' ? null : normalized;
};

const toStableComparableValue = (value) => {
    if (Array.isArray(value)) {
        return value.map(toStableComparableValue);
    }

    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((acc, key) => {
                acc[key] = toStableComparableValue(value[key]);
                return acc;
            }, {});
    }

    return value;
};

const stableSerialize = (value) => JSON.stringify(toStableComparableValue(value));

export const buildDatastreamPatchPayload = (initialDatastreams, currentDatastreams) => {
    const original = (Array.isArray(initialDatastreams) ? initialDatastreams : []).filter(Boolean);
    const current = (Array.isArray(currentDatastreams) ? currentDatastreams : []).filter(Boolean);

    const originalMap = new Map(
        original
            .map((datastream) => [normalizeDatastreamIdKey(datastream?.id), datastream])
            .filter(([idKey]) => Boolean(idKey))
    );
    const currentMap = new Map(
        current
            .map((datastream) => [normalizeDatastreamIdKey(datastream?.id), datastream])
            .filter(([idKey]) => Boolean(idKey))
    );

    const toCreate = [];
    const toUpdate = [];
    const toDelete = [];

    current.forEach((datastream) => {
        const idKey = normalizeDatastreamIdKey(datastream?.id);
        if (!idKey) return;

        const initialDatastream = originalMap.get(idKey);
        if (!initialDatastream) {
            toCreate.push(datastream);
            return;
        }

        if (stableSerialize(initialDatastream) !== stableSerialize(datastream)) {
            toUpdate.push(datastream);
        }
    });

    original.forEach((datastream) => {
        const idKey = normalizeDatastreamIdKey(datastream?.id);
        if (!idKey || currentMap.has(idKey)) return;
        toDelete.push(datastream.id);
    });

    return { toCreate, toUpdate, toDelete };
};

export const buildTemplateUpdatePayloadBase = ({
    payload,
    initialTemplate,
    templateEvaluationData,
}) => {
    const datastreamsPatch = buildDatastreamPatchPayload(
        initialTemplate?.datastreams,
        payload?.datastreams
    );

    const finalPayload = {
        ...payload,
        events: payload.events || [],
        webdashboard: payload.webdashboard || [],
        templateEvaluationData: templateEvaluationData,
        client_platform: "web",
    };

    return { finalPayload, datastreamsPatch };
};

export const hasDatastreamPatchChanges = (patch) =>
    (patch?.toCreate?.length || 0) > 0 ||
    (patch?.toUpdate?.length || 0) > 0 ||
    (patch?.toDelete?.length || 0) > 0;

const normalizeDatastreamTypeKey = (value) => String(value ?? '').trim().toLowerCase();

export const findDatastreamTypeChangesForConfirmation = (initialDatastreams, currentDatastreams) => {
    const original = (Array.isArray(initialDatastreams) ? initialDatastreams : []).filter(Boolean);
    const current = (Array.isArray(currentDatastreams) ? currentDatastreams : []).filter(Boolean);
    const originalMap = new Map(
        original
            .map((datastream) => [normalizeDatastreamIdKey(datastream?.id), datastream])
            .filter(([idKey]) => Boolean(idKey))
    );

    return current
        .map((datastream) => {
            const idKey = normalizeDatastreamIdKey(datastream?.id);
            const initialDatastream = idKey ? originalMap.get(idKey) : null;
            if (!initialDatastream) return null;

            const previousType = normalizeDatastreamTypeKey(initialDatastream.dataType);
            const nextType = normalizeDatastreamTypeKey(datastream?.dataType);

            if (previousType === nextType) return null;

            return {
                id: idKey,
                name: String(initialDatastream.name || datastream?.name || initialDatastream.pin || datastream?.pin || '').trim(),
                pin: String(initialDatastream.pin || datastream?.pin || '').trim(),
                fromType: previousType,
                toType: nextType,
            };
        })
        .filter(Boolean);
};

export const pinCategories = ['Virtual Pin', 'Enum', 'Location', 'Digital Pin', 'Analog Pin'];

export const checkTabPermission = (userRole, allowedRoles) => {
    const role = userRole?.toLowerCase();

    if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
        return false;
    }

    return allowedRoles.map((allowedRole) => allowedRole.toLowerCase()).includes(role);
};

export const buildTemplateDetailsTabs = ({
    labels,
    icons,
    canViewDatastreams,
    canViewEvents,
}) => {
    const availableTabs = [];
    availableTabs.push({ name: labels.home, icon: icons.home });
    if (canViewDatastreams) {
        availableTabs.push({ name: labels.datastreams, icon: icons.datastreams });
    }
    availableTabs.push({ name: labels.webdashboard, icon: icons.webdashboard });
    if (canViewEvents) {
        availableTabs.push({ name: labels.eventsnotifications, icon: icons.eventsnotifications });
    }
    return availableTabs;
};

const protectedDashboardAccessRoles = ['super', 'owner'];

export const toggleDashboardAccessRole = (roles, role) => {
    const nextRoles = new Set(Array.isArray(roles) ? roles : []);

    if (protectedDashboardAccessRoles.includes(role)) {
        return Array.from(nextRoles);
    }

    if (nextRoles.has(role)) {
        nextRoles.delete(role);
    } else {
        nextRoles.add(role);
    }

    nextRoles.add('super');
    nextRoles.add('owner');

    return Array.from(nextRoles);
};

export const toggleDashboardAccessRoleState = (state, role, rolesKey) => {
    if (!state) return state;
    if (protectedDashboardAccessRoles.includes(role)) return state;

    return {
        ...state,
        [rolesKey]: toggleDashboardAccessRole(state[rolesKey], role),
    };
};

export const applyEditedDashboardTabRoles = (details, editingTabRoleData) => {
    if (!editingTabRoleData) return details;

    const updatedWebDashboard = [...(details.webdashboard || [])];
    const current = updatedWebDashboard[editingTabRoleData.index];
    if (!current) return details;

    const nextNameRaw = typeof editingTabRoleData.name === 'string' ? editingTabRoleData.name.trim() : '';
    const nextName = nextNameRaw.length > 0 ? nextNameRaw : current.name;

    updatedWebDashboard[editingTabRoleData.index] = {
        ...current,
        name: nextName,
        access_role: editingTabRoleData.roles,
    };

    return { ...details, webdashboard: updatedWebDashboard };
};

export const parseTemplateDetailsUrlIndex = (value, fallback = 0) => {
    const parsed = parseInt(value);
    return !isNaN(parsed) ? parsed : fallback;
};

export const buildTemplateDashboardViewportState = ({
    tabs,
    currentTab,
    webDashboardTabName,
    details,
    isDashboardExpanded,
    isDetailsRailExpanded,
    dashboardViewportHeight,
    viewportHeight,
    editMode,
    labels,
}) => {
    const isWebDashboardTabActive =
        tabs[currentTab]?.name === webDashboardTabName &&
        Array.isArray(details?.webdashboard) &&
        details.webdashboard.length > 0;
    const isDashboardExpandedActive = isWebDashboardTabActive && isDashboardExpanded;
    const effectiveDetailsRailExpanded = isDashboardExpandedActive ? false : isDetailsRailExpanded;
    const fallbackDashboardHeight = Math.max(
        280,
        viewportHeight - (isDashboardExpandedActive ? 166 : (editMode === 'edit' ? 332 : 300)),
    );
    const dashboardMaxHeight = `${dashboardViewportHeight || fallbackDashboardHeight}px`;
    const dashboardExpandLabel = isDashboardExpandedActive
        ? (labels.collapseAction || 'Click to collapse')
        : (labels.expandAction || 'Click to expand');

    return {
        isWebDashboardTabActive,
        isDashboardExpandedActive,
        effectiveDetailsRailExpanded,
        dashboardMaxHeight,
        dashboardExpandLabel,
    };
};
